import type { CreateUpdaterBatch, CreateUpdaterJob, UpdaterAnswer, UpdaterEvent, UpdaterJob, UpdaterJobGroup, UpdaterServer } from '../types/modpackUpdater';

const BASE = 'https://spy.playcdu.co/api/notes/modpack-updater';

export class ModpackUpdaterApiError extends Error {
  constructor(message: string, readonly status: number) {
    super(message);
    this.name = 'ModpackUpdaterApiError';
  }
}

async function request<T>(path: string, body?: unknown, signal?: AbortSignal): Promise<T> {
  const response = await fetch(`${BASE}${path}`, {
    method: body === undefined ? 'GET' : 'POST',
    cache: 'no-store',
    credentials: 'include',
    signal,
    ...(body === undefined ? {} : { headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) }),
  });
  return responseData<T>(response);
}

async function responseData<T>(response: Response): Promise<T> {
  const data = await response.json().catch(() => null);
  if (!response.ok) {
    throw new ModpackUpdaterApiError(
      typeof data?.error === 'string' ? data.error : `The update service could not complete this request (${response.status}).`,
      response.status,
    );
  }
  return data as T;
}

export const modpackUpdaterApi = {
  server: (address: string, signal?: AbortSignal) => request<UpdaterServer>(`/servers/${encodeURIComponent(address)}`, undefined, signal),
  create: (body: CreateUpdaterJob) => request<{ job: UpdaterJob }>('/jobs', body),
  createBatch: (body: CreateUpdaterBatch) => request<UpdaterJobGroup>('/jobs/batch', body),
  group: (id: string, signal?: AbortSignal) => request<UpdaterJobGroup>(`/groups/${encodeURIComponent(id)}`, undefined, signal),
  job: (id: string, after = 0, signal?: AbortSignal) => request<{ job: UpdaterJob; events: UpdaterEvent[] }>(`/jobs/${encodeURIComponent(id)}?after=${after}`, undefined, signal),
  answer: (id: string, requestId: string, value: UpdaterAnswer) => request<{ job: UpdaterJob; accepted: true }>(`/jobs/${encodeURIComponent(id)}/input`, { request_id: requestId, value }),
  uploadFile: async (id: string, requestId: string, file: File, signal?: AbortSignal) => {
    const response = await fetch(`${BASE}/jobs/${encodeURIComponent(id)}/files/${encodeURIComponent(requestId)}`, {
      method: 'POST', credentials: 'include', cache: 'no-store', signal,
      headers: { 'Content-Type': 'application/octet-stream' }, body: file,
    });
    return responseData<{ job: UpdaterJob; accepted: true }>(response);
  },
  cancel: (id: string) => request<{ job: UpdaterJob }>(`/jobs/${encodeURIComponent(id)}/cancel`, {}),
  recover: (id: string, action: 'rollback' | 'resume' = 'rollback') => request<{ job: UpdaterJob }>(`/jobs/${encodeURIComponent(id)}/recover`, { action }),
};
